import { useState, useEffect, useRef } from 'react';
import { collection, query, orderBy, onSnapshot, addDoc, updateDoc, doc, serverTimestamp, where } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import styles from './ChatAdminPage.module.css';

const TAB_ACTIVE = 'active';
const TAB_PENDING = 'pending';

function ChatAdminPage() {
  const [conversations, setConversations] = useState([]);
  const [activeTab, setActiveTab] = useState(TAB_ACTIVE);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  // Vérifier si l'utilisateur est admin
  const displayName = currentUser?.displayName || currentUser?.email?.split('@')[0] || '';
  const isAdminUser = currentUser && displayName?.toLowerCase() === 'admin';

  useEffect(() => {
    if (!isAdminUser) {
      navigate('/');
      return;
    }

    // Écouter toutes les conversations
    const conversationsRef = collection(db, 'chatConversations');
    const q = query(conversationsRef, orderBy('updatedAt', 'desc'));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const convs = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setConversations(convs);
    });

    return () => unsubscribe();
  }, [isAdminUser, navigate]);

  // Écouter les messages de la conversation sélectionnée
  useEffect(() => {
    if (!selectedConversation) {
      setMessages([]);
      return;
    }

    const messagesRef = collection(db, 'chatConversations', selectedConversation.id, 'messages');
    const q = query(messagesRef, orderBy('createdAt', 'asc'));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const msgs = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setMessages(msgs);

      // Marquer les messages comme lus (côté admin, on marque les messages de l'utilisateur comme lus)
      msgs.forEach((msg) => {
        if (msg.sender === 'user' && !msg.read) {
          updateDoc(
            doc(db, 'chatConversations', selectedConversation.id, 'messages', msg.id),
            {
              read: true,
            },
          );
        }
      });

      // Mettre à jour le unreadCount
      updateDoc(doc(db, 'chatConversations', selectedConversation.id), {
        unreadCount: 0,
      });
    });

    return () => unsubscribe();
  }, [selectedConversation]);

  // Scroll automatique vers le bas
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedConversation || sending) return;

    setSending(true);
    try {
      const messagesRef = collection(db, 'chatConversations', selectedConversation.id, 'messages');
      await addDoc(messagesRef, {
        text: newMessage.trim(),
        sender: 'admin',
        adminName: currentUser?.displayName || 'Admin',
        createdAt: serverTimestamp(),
        read: false, // marquer comme non lu côté user jusqu'à ce qu'il ouvre le chat
      });

      // Mettre à jour la conversation (dernier message = admin)
      await updateDoc(doc(db, 'chatConversations', selectedConversation.id), {
        updatedAt: serverTimestamp(),
        status: 'active',
        lastMessageSender: 'admin',
      });

      setNewMessage('');
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Erreur lors de l\'envoi du message:', error);
      alert('Erreur lors de l\'envoi du message. Veuillez réessayer.');
    } finally {
      setSending(false);
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return "Aujourd'hui";
    }
    if (date.toDateString() === yesterday.toDateString()) {
      return 'Hier';
    }
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' });
  };

  const activeConversations = conversations.filter((c) => (c.status || '') === 'active');
  const pendingConversations = conversations.filter((c) => (c.status || '') === 'pending');
  const displayedConversations = activeTab === TAB_PENDING ? pendingConversations : activeConversations;
  const unreadCountActive = activeConversations.reduce((sum, conv) => sum + (conv.unreadCount || 0), 0);
  const unreadCountPending = pendingConversations.reduce((sum, conv) => sum + (conv.unreadCount || 0), 0);

  const isUserLastReply = (conv) => conv.lastMessageSender === 'user';

  if (!isAdminUser) {
    return null;
  }

  return (
    <div className={styles.page}>
      <div className={styles.container}>
        <div className={styles.layout}>
          {/* Liste des conversations */}
          <div className={styles.conversationsList}>
            <div className={styles.conversationsHeader}>
              <h1>Messages</h1>
              {activeTab === TAB_ACTIVE && unreadCountActive > 0 && (
                <span className={styles.unreadBadge}>{unreadCountActive}</span>
              )}
              {activeTab === TAB_PENDING && unreadCountPending > 0 && (
                <span className={styles.unreadBadge}>{unreadCountPending}</span>
              )}
            </div>
            <div className={styles.tabs}>
              <button
                type="button"
                className={`${styles.tab} ${activeTab === TAB_ACTIVE ? styles.tabActive : ''}`}
                onClick={() => setActiveTab(TAB_ACTIVE)}
              >
                Actifs
                {unreadCountActive > 0 && (
                  <span className={styles.tabUnread}>{unreadCountActive}</span>
                )}
              </button>
              <button
                type="button"
                className={`${styles.tab} ${activeTab === TAB_PENDING ? styles.tabActive : ''}`}
                onClick={() => setActiveTab(TAB_PENDING)}
              >
                En attente
                {unreadCountPending > 0 && (
                  <span className={styles.tabUnread}>{unreadCountPending}</span>
                )}
              </button>
            </div>
            <div className={styles.conversations}>
              {displayedConversations.length === 0 ? (
                <div className={styles.emptyState}>
                  <p>
                    {activeTab === TAB_PENDING
                      ? 'Aucune conversation en attente'
                      : 'Aucune conversation'}
                  </p>
                </div>
              ) : (
                displayedConversations.map((conv) => (
                  <button
                    key={conv.id}
                    type="button"
                    className={`${styles.conversationItem} ${selectedConversation?.id === conv.id ? styles.conversationItemActive : ''} ${isUserLastReply(conv) ? styles.conversationItemUserLastReply : ''}`}
                    onClick={() => setSelectedConversation(conv)}
                  >
                    <div className={styles.conversationInfo}>
                      <div className={styles.conversationName}>
                        {conv.userDisplayName || conv.visitorName || 'Anonyme'}
                      </div>
                      <div className={styles.conversationEmail}>
                        {conv.userEmail || conv.visitorEmail || ''}
                      </div>
                      <div className={styles.conversationMeta}>
                        <span className={styles.conversationStatus}>{conv.status}</span>
                        <span className={styles.conversationDate}>{formatDate(conv.updatedAt)}</span>
                      </div>
                    </div>
                    {conv.unreadCount > 0 && (
                      <span className={styles.conversationUnread}>{conv.unreadCount}</span>
                    )}
                  </button>
                ))
              )}
            </div>
          </div>

          {/* Zone de chat */}
          <div className={styles.chatArea}>
            {selectedConversation ? (
              <>
                <div className={styles.chatHeader}>
                  <div>
                    <h2>
                      {selectedConversation.userDisplayName ||
                        selectedConversation.visitorName ||
                        'Anonyme'}
                    </h2>
                    <p>{selectedConversation.userEmail || selectedConversation.visitorEmail}</p>
                  </div>
                  <button
                    type="button"
                    className={styles.closeChatButton}
                    onClick={() => setSelectedConversation(null)}
                  >
                    Fermer
                  </button>
                </div>

                <div className={styles.messagesContainer}>
                  {messages.length === 0 ? (
                    <div className={styles.emptyState}>
                      <p>Aucun message dans cette conversation</p>
                    </div>
                  ) : (
                    messages.map((msg) => (
                      <div
                        key={msg.id}
                        className={`${styles.message} ${msg.sender === 'admin' ? styles.messageAdmin : styles.messageVisitor}`}
                      >
                        <div className={styles.messageContent}>
                          <p>{msg.text}</p>
                          <span className={styles.messageTime}>{formatTime(msg.createdAt)}</span>
                        </div>
                      </div>
                    ))
                  )}
                  <div ref={messagesEndRef} />
                </div>

                <form onSubmit={handleSendMessage} className={styles.messageForm}>
                  <input
                    type="text"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Tapez votre réponse..."
                    className={styles.messageInput}
                    disabled={sending}
                  />
                  <button
                    type="submit"
                    className={styles.sendButton}
                    disabled={!newMessage.trim() || sending}
                  >
                    {sending ? '...' : (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" />
                      </svg>
                    )}
                  </button>
                </form>
              </>
            ) : (
              <div className={styles.noSelection}>
                <p>Sélectionnez une conversation pour commencer</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ChatAdminPage;

